export type RevocationReason =
  | 'authorization_revoked'
  | 'user_removed'
  | 'notification_failures_exceeded'
  | 'version_removed'

export type RemediationAction =
  | 'reauth'
  | 'reconnect'
  | 'remove'
  | 'support'

export interface RevocationGuidance {
  title: string
  message: string
  action: RemediationAction
  actionLabel: string
}

const FALLBACK: RevocationGuidance = {
  title: 'Subscription disconnected',
  message:
    "We don't have specific guidance for this revocation reason. " +
    'Try reauthorizing the bot and reconnecting channels.',
  action: 'reauth',
  actionLabel: 'Reauthorize on Twitch'
}

const GUIDANCE: Record<RevocationReason, RevocationGuidance> = {
  authorization_revoked: {
    title: 'Bot authorization was revoked',
    message:
      'Twitch removed our access. Usually because you enabled 2FA, ' +
      'changed your password, or disconnected the app at ' +
      'twitch.tv/settings/connections. Reauthorize the bot, then ' +
      'reconnect any channels that failed.',
    action: 'reauth',
    actionLabel: 'Reauthorize on Twitch'
  },
  notification_failures_exceeded: {
    title: 'Subscription dropped after repeated webhook failures',
    message:
      'Twitch stopped delivering events because our webhook returned ' +
      'errors too many times in a row. The OAuth grant is still valid ' +
      ' just reconnect to recreate the subscription.',
    action: 'reconnect',
    actionLabel: 'Reconnect channels'
  },
  user_removed: {
    title: 'Twitch account no longer exists',
    message:
      "The bot or broadcaster account was deleted on Twitch. There's " +
      'nothing to reconnect to, remove this binding from any streams ' +
      'still referencing it.',
    action: 'remove',
    actionLabel: 'Remove binding'
  },
  version_removed: {
    title: 'Subscription type deprecated by Twitch',
    message:
      'Twitch retired the EventSub version this subscription used. ' +
      'A platform update is needed, please contact support.',
    action: 'support',
    actionLabel: 'Contact support'
  }
}

export const guidanceFor = (reason: string | null | undefined): RevocationGuidance => {
  if (reason == null) return FALLBACK
  const known = GUIDANCE[reason as RevocationReason]
  return known ?? FALLBACK
}

const CHANNEL_AUTHORIZATION_REVOKED: RevocationGuidance = {
  title: 'Twitch access for this channel ended',
  message:
    'Either the streamer removed FlightDeck in their Twitch settings, or the bot lost its own ' +
    'Twitch access. FlightDeck checks both: if the Admin page says Reauthorize needed, ' +
    'reauthorize the bot there; otherwise the streamer reconnects Twitch on the Connections page. ' +
    'Then reconnect the channel on the Admin page.',
  action: 'reconnect',
  actionLabel: 'Open the Admin page'
}

export const channelGuidanceFor = (reason: string | null | undefined): RevocationGuidance =>
  reason === 'authorization_revoked' ? CHANNEL_AUTHORIZATION_REVOKED : guidanceFor(reason)
